import { useEffect, useState } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Gift, Copy, Check, Sparkles, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReferralCard() {
  const [stats, setStats] = useState(null);
  const [code, setCode] = useState(null);
  const [redeemInput, setRedeemInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        apiFetch(`${API_URL}/api/referrals/code`),
        apiFetch(`${API_URL}/api/referrals/stats`),
      ]);
      if (c.ok) setCode(await c.json());
      if (s.ok) setStats(await s.json());
    } catch (_e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const handleCopy = async () => {
    if (!code?.share_url) return;
    try {
      await navigator.clipboard.writeText(code.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Referral link copied');
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const handleRedeem = async () => {
    if (!redeemInput.trim()) return;
    setRedeeming(true);
    try {
      const res = await apiFetch(`${API_URL}/api/referrals/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: redeemInput.trim().toUpperCase() }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || 'Referral redeemed — $10 of premium AI credited to both of you');
        setRedeemInput('');
        await load();
      } else {
        toast.error(d.detail || 'Could not redeem code');
      }
    } catch (e) {
      toast.error('Redeem failed: ' + e.message);
    }
    setRedeeming(false);
  };

  if (!code) return null;

  const alreadyRedeemed = !!stats?.referred_by;

  return (
    <Card data-testid="referral-card" className="overflow-hidden border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-transparent to-blue-500/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Gift className="w-5 h-5 text-emerald-500" />
          Refer a friend — both get ${code.bonus_usd} of premium AI
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Share your code below. When your friend signs up and redeems it, <strong>both of you instantly get ${code.bonus_usd} of Claude/GPT-4o credit</strong> on top of your plan cap. Unlimited referrals.
        </p>

        {/* Share */}
        <div className="rounded-lg border bg-muted/40 p-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Your code</p>
            <p className="font-mono text-xl font-bold tracking-wider" data-testid="referral-my-code">{code.code}</p>
            <p className="text-[11px] text-muted-foreground truncate mt-1">{code.share_url}</p>
          </div>
          <Button onClick={handleCopy} size="sm" variant="outline" className="gap-1.5" data-testid="referral-copy-btn">
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3" data-testid="referral-stats">
          <div className="rounded-lg border p-3 text-center">
            <Users className="w-4 h-4 mx-auto text-muted-foreground" />
            <p className="text-2xl font-bold mt-1">{stats?.referrals_count ?? 0}</p>
            <p className="text-[11px] text-muted-foreground">Friends referred</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <DollarSign className="w-4 h-4 mx-auto text-muted-foreground" />
            <p className="text-2xl font-bold mt-1 text-emerald-500">${stats?.total_earned_usd?.toFixed(2) ?? '0.00'}</p>
            <p className="text-[11px] text-muted-foreground">Total earned</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <Sparkles className="w-4 h-4 mx-auto text-muted-foreground" />
            <p className="text-2xl font-bold mt-1 text-violet-500">${stats?.credit_balance_usd?.toFixed(2) ?? '0.00'}</p>
            <p className="text-[11px] text-muted-foreground">Unused credit</p>
          </div>
        </div>

        {/* Redeem someone else's code */}
        <div className="pt-2 border-t">
          {alreadyRedeemed ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Check className="w-3 h-3 text-emerald-500" />
              You redeemed code <Badge variant="outline" className="font-mono">{stats.referred_by}</Badge> — bonus already applied.
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                value={redeemInput}
                onChange={(e) => setRedeemInput(e.target.value.toUpperCase())}
                placeholder="Got a referral code? Paste it"
                className="font-mono uppercase"
                maxLength={12}
                data-testid="referral-redeem-input"
              />
              <Button
                onClick={handleRedeem}
                disabled={!redeemInput.trim() || redeeming}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="referral-redeem-btn"
              >
                {redeeming ? 'Redeeming…' : 'Redeem'}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
